import axios, { AxiosError, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';

import { AuthCredentials } from '~src/types/db';
import Cookies from 'universal-cookie';
import { __DEV__ } from '~src/config';
import { appLog } from '~src/utils/log';
import { UserCrVM } from '~components/popups-modals/CachedSharesModals/TenderClaimModals/CreateUserClaim';
import {
  normalizeMsWordDashes,
  sanitizeAxiosConfigMsWordDashes,
} from '~src/hooks/ms-dash-sanitization';
import {
  getAuthCookie,
  removeAuthCookie,
  setAuthCookie,
} from '~src/hooks/utils/authCookie';

export const getCookieName = () => {
  switch (process.env.REACT_APP_STAGE) {
    case 'local':
    case 'local ':
      return 'local-apitoken'; //return 'staging-apitoken'; STAGE_OLD
    case 'development':
    case 'development ':
      return 'dev-apitoken';
    case 'staging':
      return 'staging-apitoken';
    case 'production':
      return 'app-apitoken';
    case 'tester':
      return 'aws_tester-apitoken';
  }
};

const getApiPath = () => {
  switch (process.env.REACT_APP_STAGE) {
    case 'local':
    case 'local ':
      return 'http://localhost/c-web/api/v1'; // return 'https://staging.c-web.dk/c-web/api/v1'; STAGE_OLD
    case 'development':
    case 'development ':
      return 'https://dev.c-web.dk/c-web/api/v1';
    case 'staging':
      return 'https://staging.c-web.dk/c-web/api/v1';
    case 'production':
      return 'https://app.c-web.dk/c-web/api/v1';
    case 'tester':
      return 'https://dev.c-web.dk/c-web/api/v1';
  }
};

const getRedirectPath = () => {
  switch (process.env.REACT_APP_STAGE) {
    case 'local':
    case 'local ':
      return 'http://localhost/'; //return 'https://staging.c-web.dk'; STAGE_OLD
    case 'development':
    case 'development ':
      return 'https://dev.c-web.dk';
    case 'staging':
      return 'https://staging.c-web.dk';
    case 'production':
      return 'https://app.c-web.dk';
  }
};

export const webAppPath = getRedirectPath() + '/c-web/';
export const appRoot = getRedirectPath();

const cookies = new Cookies();

// const TOKEN_KEY = 'tiktoken';
const TOKEN_KEY = getCookieName();

const bearerPrefix = 'Bearer ';
// const initialToken = sessionStorage.getItem(TOKEN_KEY); // v1
// const initialToken = cookies.get(TOKEN_KEY!); // v2
const initialToken = getAuthCookie(TOKEN_KEY as string, cookies); // v3 global domain

let shouldRetry = true;

export const apiCweb = axios.create({
  // TODO: move in env
  baseURL: getApiPath(),
  timeout: 10000,
});

if (initialToken) {
  apiCweb.defaults.headers.common.Authorization = bearerPrefix + initialToken;
}

// IMPORTANT: put this interceptor BEFORE any auth/refresh logic etc.
apiCweb.interceptors.request.use(
  (config) => sanitizeAxiosConfigMsWordDashes(config),
  (error) => Promise.reject(error)
);

axiosRetry(apiCweb, {
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // don't retry for GET, POST
    if (['GET', 'POST'].includes(error.config.method?.toUpperCase() || ''))
      return false;
    return !error.response;
  },
});

// Add a response interceptor
apiCweb.interceptors.response.use(
  (response) => {
    shouldRetry = true;
    return response;
  },
  async (error: AxiosError) => {
    const pathname = window.location.pathname;
    const isCachedShareRoute = pathname.includes('invitations');
    const isShareEmailLinkRoute = pathname.includes('share-keys');
    const isRootDashboardRoute = pathname === '/';
    const isPublicTenderRoute = pathname.includes('public-tenders');

    // 404
    if (error.response?.status === 404) {
      // для cached-shares не редіректимо
      if (
        isCachedShareRoute ||
        isShareEmailLinkRoute ||
        isRootDashboardRoute ||
        isPublicTenderRoute
      ) {
        return Promise.reject(error);
      }

      window.location.pathname = '/404';
      return apiCweb.request(error.config);
    }

    // 401
    if (error.response?.status === 401 && pathname !== '/login') {
      // для cached-shares не редіректимо на логін
      if (
        isCachedShareRoute ||
        isShareEmailLinkRoute ||
        isRootDashboardRoute ||
        isPublicTenderRoute
      ) {
        return Promise.reject(error);
      }

      removeAuthCookie(getCookieName() as string, cookies);
      localStorage.setItem('location', pathname);
      apiCweb.defaults.headers.common.Authorization = '';
      window.location.pathname = '/login';
      return apiCweb.request(error.config);
    }

    // 403
    if (error.response?.status === 403) {
      // для cached-shares не редіректимо (даємо обробити як "expired")
      if (isCachedShareRoute || isShareEmailLinkRoute || isPublicTenderRoute) {
        return Promise.reject(error);
      }

      if (shouldRetry) {
        shouldRetry = false;
        return apiCweb.request(error.config);
      }
    }

    return Promise.reject(error);
  }
);

export interface createUserParam {
  data: UserCrVM;
  shareId: string;
  shareKey: string;
}

/** Register user */
export const registerUser = async ({
  shareId,
  shareKey,
  data,
}: createUserParam) => {
  const res = await apiCweb.post(`/tender_shares/${shareId}/user`, data, {
    headers: {
      'Share-Key': shareKey,
    },
  });
  return res.data;
};

/** Login request */
export const loginReq = async (data: AuthCredentials) => {
  const res = await apiCweb.post<string>('/auth/login', data);
  return res.data;
};

const testLoginData: AuthCredentials = {
  username: 'ihor.kindgeek@detaljen.dk',
  password: '4h3ywgFHHZPIEQV',
};

/** Does loginReq and stores token in storage & axios instance  */
export const getNewToken = async (data) => {
  const token = await loginReq(
    __DEV__ && data === undefined ? testLoginData : data
  );
  if (token) {
    // cookies.set('dev-apitoken', token);
    setAuthCookie(getCookieName() as string, token, cookies);
    apiCweb.defaults.headers.common.Authorization = bearerPrefix + token;
  }

  const originalLocation = localStorage.getItem('location');
  if (originalLocation) {
    window.location.pathname = originalLocation;
    localStorage.removeItem('location');
  }
};
